import { Table, Tag } from 'antd';
import React, { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DAYTIME_FORMAT } from '../../constants';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';

const FIXED_KEYS = ['name', 'action'];

export default function AdminUsersTable({
  withAction,
  dataSource,
  selectedRowKeys,
  onSelectChange,
  configRef,
  onPageSizeChange,
  ...props
}) {
  const { t: tc } = useTranslation('common');
  const allColumns = useMemo(() => {
    const cols = [
      {
        title: tc('name'),
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        className: 'border-right',
      },
      {
        title: tc('email'),
        dataIndex: 'email',
        key: 'email',
        width: 250,
      },
      {
        title: '2FA',
        dataIndex: 'totpEnabled',
        key: 'totpEnabled',
        width: 110,
        render: (value) => (value ? <Tag color="green">{tc('enabled', 'Enabled')}</Tag> : <Tag>{'—'}</Tag>),
      },
      {
        title: tc('updatedAt'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 250,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
      {
        title: tc('createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 200,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
    ];

    if (withAction) {
      cols.push({
        key: 'action',
        width: 120,
        className: 'border-left',
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
      });
    }

    return cols;
  }, [withAction]);

  const columnConfig = useColumnConfig('admin-users-columns', allColumns, FIXED_KEYS);

  // Expose openDrawer to parent via ref
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="table-job-orders table-common">
      <Table
        columns={columnConfig.visibleColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-dark' : 'table-row-light')}
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={(size) => {
          columnConfig.setPageSize(size);
          // Server-paginated page — tell it to refetch with the new take.
          onPageSizeChange?.(size);
        }}
      />
    </div>
  );
}
